import { Button, Chip, Select, SelectItem, useDisclosure } from "@heroui/react";
import { useCallback, useEffect, useState } from "react";
import { FaArrowLeft, FaScaleBalanced } from "react-icons/fa6";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { api } from "@/api";
import type {
  ConflictListItem,
  ListConflictsQuery,
} from "@/api/accounting/conflicts";
import type { AccountingIntegration } from "@/api/accounting/integrations";
import { ResolveConflictModal } from "@/app/components/modals/accounting/resolveConflictModal";
import {
  conflictStatusColors,
  conflictStatusLocale,
  conflictTypeColors,
  conflictTypeLocale,
  entityTypeLocale,
} from "@/app/components/modals/accounting/ui";
import { TableBuilder } from "@/app/components/tables/tableBuilder";
import { ColumnType } from "@/app/components/tables/tableTypes";
import useErrorHandling from "@/hooks/useErrorHandling";

const statusOptions = [
  { key: "open", label: "Открытые" },
  { key: "resolved", label: "Разрешённые" },
  { key: "", label: "Все" },
];

export function AccountingConflictsPage() {
  const { id } = useParams();
  const integrationId = Number(id);
  const navigate = useNavigate();
  const handleError = useErrorHandling();

  const [integration, setIntegration] = useState<AccountingIntegration | null>(
    null,
  );
  const [conflicts, setConflicts] = useState<ConflictListItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [conflictId, setConflictId] = useState<number | null>(null);
  const [filters, setFilters] = useState<ListConflictsQuery>({
    status: "open",
  });

  const resolveModalDisclosure = useDisclosure();

  const loadConflicts = useCallback(() => {
    if (!integrationId) return;
    setIsLoading(true);
    api.accountingConflicts
      .list(integrationId, filters)
      .then(setConflicts)
      .catch((err) => {
        const { errorMessage } = handleError(err);
        toast.error(errorMessage);
      })
      .finally(() => setIsLoading(false));
  }, [integrationId, filters, handleError]);

  useEffect(() => {
    loadConflicts();
  }, [loadConflicts]);

  useEffect(() => {
    if (!integrationId) return;
    api.accountingIntegrations
      .get(integrationId)
      .then(setIntegration)
      .catch(() => setIntegration(null));
  }, [integrationId]);

  const setFilter = useCallback(
    (key: keyof ListConflictsQuery, value: string) => {
      setFilters((current) => ({ ...current, [key]: value || undefined }));
    },
    [],
  );

  return (
    <>
      <ResolveConflictModal
        disclosure={resolveModalDisclosure}
        integrationId={integrationId}
        conflictId={conflictId}
        onResolved={loadConflicts}
      />

      <div className="flex flex-row flex-wrap gap-4 w-full">
        <div className="flex-grow bg-white dark:bg-zinc-900 rounded-xl border-2 border-foreground-200 dark:border-foreground-100 p-4">
          <div className="flex flex-row justify-between w-full items-center mb-4">
            <div className="flex flex-row gap-4 items-center">
              <span className="font-medium">
                Конфликты синхронизации
                {integration ? `: ${integration.displayName}` : ""}
              </span>
            </div>
            <div className="flex flex-row gap-4 items-center">
              <Button
                startContent={<FaArrowLeft />}
                size="sm"
                variant="flat"
                className="max-w-fit"
                onPress={() => navigate("/dashboard/accounting/integrations")}>
                К интеграциям
              </Button>
            </div>
          </div>

          <div className="flex flex-row flex-wrap gap-4 mb-4">
            <Select
              label="Статус"
              size="sm"
              variant="bordered"
              className="max-w-xs"
              disallowEmptySelection
              selectedKeys={[filters.status || ""]}
              onSelectionChange={(keys) =>
                setFilter("status", String([...keys][0] ?? ""))
              }>
              {statusOptions.map((option) => (
                <SelectItem key={option.key}>{option.label}</SelectItem>
              ))}
            </Select>
            <Select
              label="Тип"
              size="sm"
              variant="bordered"
              className="max-w-xs"
              disallowEmptySelection
              selectedKeys={[filters.type || ""]}
              onSelectionChange={(keys) =>
                setFilter("type", String([...keys][0] ?? ""))
              }>
              {[
                <SelectItem key="">Все</SelectItem>,
                ...Object.entries(conflictTypeLocale).map(([key, label]) => (
                  <SelectItem key={key}>{label}</SelectItem>
                )),
              ]}
            </Select>
            <Select
              label="Сущность"
              size="sm"
              variant="bordered"
              className="max-w-xs"
              disallowEmptySelection
              selectedKeys={[filters.entityType || ""]}
              onSelectionChange={(keys) =>
                setFilter("entityType", String([...keys][0] ?? ""))
              }>
              {[
                <SelectItem key="">Все</SelectItem>,
                ...Object.entries(entityTypeLocale).map(([key, label]) => (
                  <SelectItem key={key}>{label}</SelectItem>
                )),
              ]}
            </Select>
          </div>

          <div className="flex flex-col gap-2 mb-2">
            <TableBuilder
              displayOptions={false}
              removeWrapper={true}
              columns={[
                {
                  key: "conflictId",
                  label: "#",
                  type: ColumnType.Number,
                  sortable: true,
                },
                {
                  key: "type",
                  label: "Тип",
                  type: ColumnType.Custom,
                  sortable: true,
                  render: (value: string) => (
                    <Chip
                      size="sm"
                      variant="flat"
                      color={conflictTypeColors[value] || "default"}>
                      {conflictTypeLocale[value] || value}
                    </Chip>
                  ),
                },
                {
                  key: "entityType",
                  label: "Сущность",
                  type: ColumnType.Custom,
                  sortable: true,
                  render: (value: string, row: ConflictListItem) => (
                    <div className="flex flex-col">
                      <span>{entityTypeLocale[value] || value}</span>
                      <span className="text-tiny text-default-400">
                        {row.domainId ? `#${row.domainId}` : "нет в Elastic"}
                      </span>
                    </div>
                  ),
                },
                {
                  key: "reason",
                  label: "Причина",
                  type: ColumnType.String,
                },
                {
                  key: "status",
                  label: "Статус",
                  type: ColumnType.Custom,
                  sortable: true,
                  render: (value: string) => (
                    <Chip
                      size="sm"
                      variant="flat"
                      color={conflictStatusColors[value] || "default"}>
                      {conflictStatusLocale[value] || value}
                    </Chip>
                  ),
                },
                {
                  key: "detectedAt",
                  label: "Обнаружен",
                  type: ColumnType.Custom,
                  sortable: true,
                  render: (value: string) => (
                    <span>
                      {new Date(value).toLocaleString("ru-RU", {
                        timeZone: "Etc/Utc",
                      })}
                    </span>
                  ),
                },
                {
                  key: "actions",
                  label: "Действия",
                  type: ColumnType.Actions,
                  actions: [
                    {
                      icon: <FaScaleBalanced />,
                      onClick: (_value, row: ConflictListItem) => {
                        setConflictId(row.conflictId);
                        resolveModalDisclosure.onOpen();
                      },
                    },
                  ],
                },
              ]}
              data={isLoading ? [] : conflicts}
              rowsPerPage={10}
              defaultSortDescriptor={{
                column: "detectedAt",
                direction: "descending",
              }}
            />
          </div>
        </div>
      </div>
    </>
  );
}
